/**
 * O quadro do funil: colunas fixas, uma por etapa, e o arraste dos cards entre elas.
 *
 * Porte de `renderizarKanban` (`admin.html:6578-6596`) e dos ouvintes de arraste do card
 * (`admin.html:6893-6932`), com o CSS do quadro de `admin.html:898-927`.
 *
 * O ARRASTE EXISTE DUAS VEZES, como no legado, porque são dois caminhos de código diferentes:
 *   · MOUSE, pelo arrastar do HTML5 (`dragstart`, `dragover`, `drop`). Ele NÃO dispara em tela de
 *     toque, e por isso sozinho deixaria o quadro travado no celular.
 *   · TOQUE, por Pointer Events filtrados por `pointerType === 'touch'` (`pointerdown`,
 *     `pointermove`, `pointerup`, `pointercancel`), com o ponteiro capturado pelo card e a coluna
 *     de destino achada por `elementFromPoint`. O card tem `touch-action: none` (`admin.html:924`)
 *     para o navegador não transformar o gesto em rolagem.
 * Os dois chegam ao mesmo lugar, `mover`, que é o `moverLeadParaStatus` do legado.
 */

// Estado e referência do React.
import { useRef, useState, type ReactNode } from 'react';
// Os rótulos das colunas.
import { STATUS_LEAD } from './constantes.js';
// Quais colunas aceitam soltar em cada caminho, com o achado 1 isolado lá.
import { colunaAceitaSoltarComMouse, colunaAceitaSoltarComToque, type LeadDoQuadro, type RecorteDoPapel } from './regras.js';

// O que, dentro do card, NÃO começa o arraste por toque (`admin.html:6905`): tocar num campo, num
// botão, num link ou no bloco de produtos é usar o controle, e não arrastar o card.
const NAO_ARRASTA = 'select, button, input, textarea, a, details, summary';

export interface KanbanProps {
  // As colunas a desenhar, na ordem (todas as onze, ou só a do filtro vindo do Painel).
  readonly colunas: readonly string[];
  // Os leads em memória, na ordem de chegada (mais novo primeiro).
  readonly leads: readonly LeadDoQuadro[];
  // O que cada papel pode arrastar.
  readonly recorte: RecorteDoPapel;
  // A chave que troca a cada `renderizar()`, e remonta os cards como o `innerHTML` do legado.
  readonly geracao: number;
  // O miolo de cada card.
  readonly desenharCartao: (lead: LeadDoQuadro) => ReactNode;
  // O movimento, igual nos três caminhos (select, mouse e toque).
  readonly mover: (leadId: string, novoStatus: string) => Promise<void>;
}

export function Kanban({ colunas, leads, recorte, geracao, desenharCartao, mover }: KanbanProps) {
  // A coluna com o destaque de "solte aqui" (`.arraste-sobre`, `admin.html:920`).
  const [colunaSobre, setColunaSobre] = useState<string | null>(null);
  // O card que está sendo arrastado, a 40% de opacidade (`.arrastando`, `admin.html:927`).
  const [arrastando, setArrastando] = useState<number | null>(null);
  // O id do card no arraste por toque: `touchDragId` do legado (`admin.html:6902`). Numa referência,
  // porque o `pointermove` precisa dele na mesma hora, sem esperar o próximo desenho.
  const toque = useRef<string | null>(null);

  // A coluna sob o dedo: o `closest('.kanban-coluna')` do legado, aqui pelo atributo da coluna.
  function statusSobOPonto(x: number, y: number): string | null {
    const elemento = document.elementFromPoint(x, y);
    return elemento?.closest('[data-status]')?.getAttribute('data-status') ?? null;
  }

  return (
    // A faixa rola na horizontal, nunca o corpo da página, e encaixa por coluna (`admin.html:903-907`).
    <div data-kanban className="flex snap-x snap-proximity items-start gap-cx-4 overflow-x-auto pb-cx-3">
      {colunas.map((status) => {
        // Os leads da coluna, na ordem de chegada (`admin.html:6583`).
        const daColuna = leads.filter((l) => l.status === status);
        // O mouse só solta onde o legado ligou os eventos (`admin.html:6596`).
        const aceitaMouse = colunaAceitaSoltarComMouse(status);
        return (
          <section
            key={status}
            data-status={status}
            aria-label={STATUS_LEAD[status] ?? status}
            // Quase a largura da tela no celular, deixando a próxima espiando (`admin.html:908`).
            className="shrink-0 grow-0 basis-[min(78vw,260px)] snap-start rounded-cx-cartao border border-borda bg-superficie-elevada"
          >
            <div className="flex items-center justify-between gap-cx-2 border-b border-borda p-cx-3 text-cx-xs font-semibold uppercase tracking-wide text-texto-suave">
              <span>{STATUS_LEAD[status] ?? status}</span>
              <span data-contagem>{daColuna.length}</span>
            </div>
            <div
              data-corpo-coluna={status}
              className={[
                'flex min-h-[70px] flex-col gap-cx-3 rounded-b-cx-cartao p-cx-3 transition-colors',
                colunaSobre === status ? 'bg-superficie-afundada' : '',
              ].join(' ')}
              onDragOver={
                aceitaMouse
                  ? (evento) => {
                      // Sem `preventDefault`, o navegador recusa o soltar.
                      evento.preventDefault();
                      setColunaSobre(status);
                    }
                  : undefined
              }
              onDragLeave={aceitaMouse ? () => setColunaSobre(null) : undefined}
              onDrop={
                aceitaMouse
                  ? (evento) => {
                      evento.preventDefault();
                      setColunaSobre(null);
                      // O id viaja no próprio arraste, como no legado (`admin.html:6593`).
                      const leadId = evento.dataTransfer.getData('text/plain');
                      void mover(leadId, status);
                    }
                  : undefined
              }
            >
              {daColuna.map((lead) => (
                <div
                  // A geração na chave remonta o card a cada redesenho do quadro.
                  key={`${geracao}-${lead.id}`}
                  data-lead-id={lead.id}
                  // O mouse arrasta pelo HTML5; marketing e consulta não (`admin.html:6609`, `3396`).
                  draggable={recorte.arrastaComMouse}
                  className={[
                    'cursor-grab touch-none rounded-cx-md border border-borda bg-superficie p-cx-3 shadow-cx-sm',
                    arrastando === lead.id ? 'opacity-40' : '',
                  ].join(' ')}
                  onDragStart={(evento) => {
                    evento.dataTransfer.setData('text/plain', String(lead.id));
                    setArrastando(lead.id);
                  }}
                  onDragEnd={() => setArrastando(null)}
                  onPointerDown={(evento) => {
                    // Só o toque entra aqui; o mouse já tem o caminho do HTML5 (`admin.html:6904`).
                    if (evento.pointerType !== 'touch' || !recorte.arrastaComToque) return;
                    if ((evento.target as Element).closest(NAO_ARRASTA)) return;
                    toque.current = String(lead.id);
                    setArrastando(lead.id);
                    // Com o ponteiro capturado, o card recebe o resto do gesto mesmo fora dele.
                    evento.currentTarget.setPointerCapture(evento.pointerId);
                  }}
                  onPointerMove={(evento) => {
                    if (evento.pointerType !== 'touch' || !toque.current) return;
                    evento.preventDefault();
                    // O destaque segue o dedo, em qualquer coluna (`admin.html:6911-6914`).
                    const corpo = document
                      .elementFromPoint(evento.clientX, evento.clientY)
                      ?.closest('[data-corpo-coluna]');
                    setColunaSobre(corpo?.getAttribute('data-corpo-coluna') ?? null);
                  }}
                  onPointerUp={(evento) => {
                    if (evento.pointerType !== 'touch' || !toque.current) return;
                    setArrastando(null);
                    setColunaSobre(null);
                    const novoStatus = statusSobOPonto(evento.clientX, evento.clientY);
                    const id = toque.current;
                    toque.current = null;
                    // Achado 1: o toque aceita "Pagamento recebido" (ver `regras.ts`).
                    if (novoStatus && colunaAceitaSoltarComToque(novoStatus)) void mover(id, novoStatus);
                  }}
                  onPointerCancel={() => {
                    // Gesto interrompido pelo sistema: limpa tudo e não move (`admin.html:6928-6932`).
                    setArrastando(null);
                    setColunaSobre(null);
                    toque.current = null;
                  }}
                >
                  {desenharCartao(lead)}
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
